import React, { useMemo, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Tag as NexusTag, SelectMenu } from '@spotoninc/nexus-react';
import {
  TypeIcon,
  ChevronUpCircleIcon,
  ReleaseIcon,
  TeamIcon,
  EndDateIcon,
  EditIcon,
} from '@spotoninc/nexus-web-assets';
import {
  getPriorityVariant,
  getStatusImage,
  getStatusLabel,
} from '../tasks.utils';
import styles from './item.module.css';
import capitalize from 'lodash/capitalize';
import { getUserList } from '../../../../redux/slices/userSlice';
import size from 'lodash/size';

/**
 * It renders info part of the details in drawer
 * @param {Object} props - Component props
 * @param {Object} props.task - Task data object
 * @param {string} props.task.status - Task status (e.g., "To do", "In Progress", "Completed")
 * @param {string} props.task.priority - Task priority (e.g., "High", "Medium", "Low")
 * @param {string} props.task.assignedToId - ID of person assigned to task
 * @param {string} props.task.dueDate - Due date string
 * @param {string} props.task.taskType - Type of task (e.g., "Summary", "Review")
 * @param {boolean} props.assignmentEditable - Whether the assignment is editable
 * @param {Function} props.handleAssignUserChange - Function to handle user assignment change
 * @param {string} props.location - label of the location (if exist then show else hide it),
 */
function Item({
  task,
  assignmentEditable = false,
  handleAssignUserChange = () => {},
}) {
  const [editMode, setEditMode] = useState(false);
  const users = useSelector(getUserList);
  const memoizedUsers = useMemo(() => {
    if (!users.length) {
      return [];
    }
    return users.map(user => ({
      label: user.first_name,
      value: user.user_id_id,
    }));
  }, [users]);

  // Use the assignedUser that was already merged by mergeUsersToTasks
  // Fall back to looking up in Redux users if not available
  const assignedUser = useMemo(() => {
    // First, check if the task already has assignedUser from mergeUsersToTasks
    if (task?.assignedUser) {
      return task.assignedUser;
    }

    // Fall back to looking up in Redux users array
    const assignedToId = task?.assignedToId;
    if (assignedToId && users.length > 0) {
      return users.find(user => user.user_id_id === assignedToId);
    }

    return null;
  }, [task?.assignedUser, task?.assignedToId, users.length]);

  // useCallback method to render assignedTo value part
  const renderAssignedToValue = useCallback(() => {
    // Get the assigned user ID directly from the task object
    const assignedToId = task?.assignedToId;

    if (assignmentEditable && users.length > 0 && assignedToId && editMode) {
      // Render SelectMenu for edit mode
      return (
        <div className={styles.detailValue}>
          <SelectMenu
            options={memoizedUsers}
            value={assignedToId}
            onChange={(_, value) => handleAssignUserChange(value)}
            placeholder="Select user"
            style={{
              width: '100%',
              minWidth: '120px',
              fontSize: '0.875rem',
              fontWeight: '400',
            }}
            className={styles.assignedToDropdown}
          />
        </div>
      );
    } else if (assignmentEditable) {
      // Render editable display mode with user name and edit icon
      return (
        <div
          className={[styles.detailValue, styles.linkContainer].join(' ')}
          onClick={e => {
            e?.preventDefault();
            setEditMode(true);
          }}
        >
          <span className={[styles.valueText, styles.linkText].join(' ')}>
            {assignedUser?.first_name || 'Unassigned'}
          </span>
          <EditIcon
            size="18px"
            color={'var(--semantic-color-icon-brand-strong-none)'}
            className={styles.editIcon}
          />
        </div>
      );
    } else {
      // Render normal UI (non-editable)
      return (
        <div className={styles.detailValue}>
          <span className={styles.valueText}>
            {assignedUser?.first_name || 'Unassigned'}
          </span>
        </div>
      );
    }
  }, [
    assignmentEditable,
    users.length,
    task?.assignedToId,
    editMode,
    memoizedUsers,
    handleAssignUserChange,
    assignedUser,
  ]);

  if (!task) {
    return <div className={styles.container}>No task data available</div>;
  }

  const priorityVariant = getPriorityVariant(task.priority);
  const statusImage = getStatusImage(task.status);
  const statusLabel = getStatusLabel(task.status);

  return (
    <div className={styles.container}>
      {/* Status Row */}
      <div className={styles.detailRow}>
        <div className={styles.detailLabel}>
          <ReleaseIcon className={styles.detailIcon} />
          <span className={styles.labelText}>Status</span>
        </div>
        <div className={styles.detailValue}>
          <div className={styles.statusValue}>
            <img
              src={statusImage}
              alt={task.status}
              className={styles.statusImage}
            />
            <span className={styles.valueText}>{statusLabel}</span>
          </div>
        </div>
      </div>

      {/* Priority Row */}
      <div className={styles.detailRow}>
        <div className={styles.detailLabel}>
          <ChevronUpCircleIcon className={styles.detailIcon} />
          <span className={styles.labelText}>Priority</span>
        </div>
        <div className={styles.detailValue}>
          <NexusTag variant={priorityVariant} size="small">
            {capitalize(task.priority)}
          </NexusTag>
        </div>
      </div>

      {/* Assigned To Row */}
      <div className={styles.detailRow}>
        <div className={styles.detailLabel}>
          <TeamIcon className={styles.detailIcon} />
          <span className={styles.labelText}>Assigned to</span>
        </div>
        {renderAssignedToValue()}
      </div>

      {/* Due Date Row */}
      <div className={styles.detailRow}>
        <div className={styles.detailLabel}>
          <EndDateIcon className={styles.detailIcon} />
          <span className={styles.labelText}>Due</span>
        </div>
        <div className={styles.detailValue}>
          <span className={styles.valueText}>{task.dueDate}</span>
        </div>
      </div>

      {/* Task Type Row */}
      <div className={styles.detailRow}>
        <div className={styles.detailLabel}>
          <TypeIcon className={styles.detailIcon} />
          <span className={styles.labelText}>Task type</span>
        </div>
        <div className={styles.detailValue}>
          <span className={styles.valueText}>{capitalize(task.taskType)}</span>
        </div>
      </div>
      {!!size(task.location) && (
        <div className={styles.detailRow}>
          <div className={styles.detailLabel}>
            <TypeIcon className={styles.detailIcon} />
            <span className={styles.labelText}>Location</span>
          </div>
          <div className={styles.detailValue}>
            <span className={styles.valueText}>
              {capitalize(task.location) || ''}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export default React.memo(Item);
